import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { EnhancedTableData, EnhancedTableHeaderData, EnhancedTableSort } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginHistoricOrder, MarginOrdersQuery } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { MarginInstrument } from '@routes/SwyftxMargin/components/General';
import { capitalize } from '@utils/formatting';

import { DateTime } from 'luxon';
import { getGroupByTitle } from 'src/lib/dynamic-price-alerts/utils/dynamic-price-alerts.utils';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginOrders } from 'src/lib/margin/hooks/orders';
import { MarginHistoryDirection } from 'src/lib/margin/types';
import { getMarketPrecisionScale } from 'src/lib/margin/utils';

import { MarginPositionsTableOpenedTime } from '../../MarginPositionsTable/components';
import { MarginHistoryTableData } from '../MarginHistoryTable.data';
import {
  MarginHistoryTableInstrumentSide,
  MarginHistoryTableLotsProfitLoss,
  MarginHistoryTableProfitLoss,
} from '../components';

type MarginPositionsHeaderData = { [key in keyof MarginHistoryTableData]: EnhancedTableHeaderData };

type Props = {
  filterStartDate?: DateTime;
  filterEndDate?: DateTime;
  filterCurrentInstrument?: string;
  filterDirections?: MarginHistoryDirection[];
  filterEvents?: string[];
  sortKey?: keyof MarginHistoryTableData;
  isSm?: boolean;
  isXs?: boolean;
};

const useMarginHistoryTable = ({
  filterStartDate,
  filterEndDate,
  filterDirections = [],
  filterCurrentInstrument,
  filterEvents = [],
  isSm,
  isXs,
  sortKey = 'lastUpdated',
}: Props) => {
  const [sort, setSort] = useState<EnhancedTableSort<MarginHistoryTableData>>({
    sortKey,
    sortDirection: 'DESC',
  });
  const [sortBy, setSortBy] = useState<MarginOrdersQuery['sortBy']>('dateTime');
  const [sortDir, setSortDir] = useState<MarginOrdersQuery['sortDir']>('desc');

  const { t } = useTranslation('margin');
  const { orders, fetchNextPage, isFetchingNextPage, isFetching, hasNextPage, isLoading, isError } = useMarginOrders({
    side: filterDirections,
    status: filterEvents,
    marketUuid: filterCurrentInstrument || undefined,
    sortBy,
    sortDir,
  });

  const { getMarketByOrder } = useMarginInstruments();
  const { currency } = useMarginAccount();
  const isDesktopView = useTailwindBreakpoint('md', true);

  const headers: MarginPositionsHeaderData = useMemo(
    () => ({
      instrument: {
        title: t('tables.history.headers.instrument'),
        alignment: 'start',
        sortable: false,
        frozen: true,
        insetLeft: true,
        offsetLeft: 0,
        showFrozenDivider: false,
        className: 'min-w-[150px]',
        enabled: isDesktopView,
      },
      type: {
        title: t('tables.history.headers.type'),
        alignment: 'start',
        sortable: false,
        frozen: true,
        showFrozenDivider: true,
        offsetLeft: 150,
        className: 'min-w-[75px]',
        enabled: isDesktopView,
      },
      side: {
        title: t('tables.history.headers.side'),
        alignment: 'start',
        sortable: false,
        enabled: isDesktopView,
      },
      actionEvent: {
        title: t('tables.history.headers.actionEvent'),
        alignment: 'start',
        sortable: false,
        enabled: isDesktopView,
      },

      lots: {
        title: t('tables.history.headers.sizeLots'),
        alignment: 'end',
        sortable: false,
        insetLeft: true,
        enabled: isDesktopView,
      },

      price: {
        title: t('tables.history.headers.price'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      profitLoss: {
        title: t('tables.history.headers.profitLoss'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      commission: {
        title: t('tables.history.headers.commission'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      lastUpdated: {
        title: t('tables.history.headers.lastUpdated'),
        alignment: 'end',
        sortable: true,
        enabled: isDesktopView,
      },

      orderId: {
        title: t('tables.history.headers.orderId'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },

      instrumentSide: {
        title: t('tables.history.headers.transactionEvent'),
        alignment: 'start',
        sortable: false,
        insetRight: true,
        enabled: !isDesktopView,
      },

      mobilePrice: {
        title: t('tables.history.headers.price'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: isSm,
      },

      mobileProfitLoss: {
        title: t('tables.history.headers.profitLoss'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: isSm,
      },

      lotsProfitLoss: {
        title: t('tables.history.headers.sizeLots'),
        alignment: 'end',
        sortable: false,
        insetRight: true,
        enabled: !isDesktopView,
      },
    }),
    [isDesktopView, isSm, t],
  );

  const filteredOrders = useMemo(() => {
    if (!orders?.length) return [];

    return orders.filter((o) => {
      const date = DateTime.fromMillis(o.dateTime || 0);

      const startDateFilter = !filterStartDate || filterStartDate < date;
      const endDateFilter = !filterEndDate || filterEndDate > date;

      return startDateFilter && endDateFilter;
    });
  }, [filterEndDate, filterStartDate, orders]);

  const buildDataItem = useCallback(
    (order: MarginHistoricOrder) => {
      const market = getMarketByOrder(order);
      const volume = order.filledVolume || order.volume;

      return {
        value: order,
        instrument: {
          value: order.marketUuid,
          element: <MarginInstrument market={market} size='medium' />,
        },
        type: {
          value: order.orderType,
          element: <Body>{capitalize(order.orderType || '-')}</Body>,
        },
        side: {
          value: order.side,
          element: (
            <Body color={order.side === 'BUY' ? 'success' : 'error'} className='whitespace-nowrap'>
              {t(`tradeSide.${order.side}` as any)}
            </Body>
          ),
        },
        actionEvent: {
          value: order.status,
          element: <Body className='whitespace-nowrap'>{t(`tradeStatus.${order.status}` as any)}</Body>,
        },
        lots: {
          value: volume,
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric>
                {!volume || Big(volume).eq('0')
                  ? '-'
                  : formatCurrency(volume, undefined, { priceScale: getMarketPrecisionScale(market?.minLotIncrement) })}
              </Numeric>
            </FlexLayout>
          ),
        },

        price: {
          value: order.fillPrice || order.price || order.stopLoss || order.takeProfit,
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric>
                {formatCurrency(order.fillPrice || order.price || order.stopLoss || order.takeProfit || '-')}
              </Numeric>
            </FlexLayout>
          ),
        },

        profitLoss: {
          value: order.closePnl,
          element: <MarginHistoryTableProfitLoss order={order} />,
        },

        commission: {
          value: order.commission,
          element: (
            <FlexLayout spacing={4} justifyContent='end'>
              <Numeric color='primary' className='whitespace-nowrap'>
                {order.commission
                  ? formatCurrency(Big(order.commission).abs(), currency, { appendCode: true, hideCode: false })
                  : '-'}
              </Numeric>
            </FlexLayout>
          ),
        },

        lastUpdated: {
          value: order.dateTime,
          groupByTitle: getGroupByTitle(DateTime.fromMillis(order.dateTime || 0)),
          element: (
            <FlexLayout justifyContent='end'>
              <MarginPositionsTableOpenedTime key={order.orderId} value={order.dateTime} />{' '}
            </FlexLayout>
          ),
        },

        orderId: {
          value: order.orderId,
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric className='whitespace-nowrap'>{order.orderId}</Numeric>
            </FlexLayout>
          ),
        },

        instrumentSide: {
          value: order.dateTime,
          groupByTitle: getGroupByTitle(DateTime.fromMillis(order.dateTime || 0)),
          element: <MarginHistoryTableInstrumentSide order={order} />,
        },

        mobilePrice: {
          value: '',
          element: (
            <FlexLayout justifyContent='end'>
              <Numeric weight='bold'>
                {formatCurrency(order.fillPrice || order.price || order.stopLoss || order.takeProfit || '-')}
              </Numeric>
            </FlexLayout>
          ),
        },

        mobileProfitLoss: {
          value: '',
          element: <MarginHistoryTableProfitLoss order={order} />,
        },

        lotsProfitLoss: {
          value: '',
          element: <MarginHistoryTableLotsProfitLoss order={order} showSubtitle={isXs} />,
        },
      };
    },
    [currency, getMarketByOrder, isXs, t],
  );

  const data: EnhancedTableData<MarginHistoryTableData, MarginHistoricOrder>[] = useMemo(() => {
    const orderData = filteredOrders.map((order: MarginHistoricOrder) => buildDataItem(order));

    return orderData;
  }, [buildDataItem, filteredOrders]);

  const onSort = useCallback(
    (newSort?: EnhancedTableSort<MarginHistoryTableData>) => {
      const tableData: EnhancedTableData<MarginHistoryTableData, MarginHistoricOrder>[] = Object.assign([], data);

      if (!newSort) return tableData;

      setSort(newSort);

      switch (newSort.sortKey) {
        case 'lastUpdated':
          setSortBy('dateTime');
          break;
      }

      setSortDir(newSort.sortDirection === 'DESC' ? 'desc' : 'asc');
    },
    [data],
  );

  return {
    initialSort: sort,
    headers,
    data,
    isLoading,
    onSort,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
  };
};

export { useMarginHistoryTable };
